import { Duration, proto3, Timestamp } from '@bufbuild/protobuf';
import { generateTypes } from '@evmos/eip712';
import { createAnyMessage } from '@evmos/proto/dist/messages/common';
import { TxContext, TxPayload, createTransactionPayload } from '@evmos/transactions';
import { EIP712TypedData } from '@evmos/transactions/dist/messages/base';
import { BasicAllowance } from 'cosmjs-types/cosmos/feegrant/v1beta1/feegrant';
import { MsgGrantAllowance } from '@buf/cosmos_cosmos-sdk.bufbuild_es/cosmos/feegrant/v1beta1/tx_pb';
import {
  BasicAllowance as BasicAllowanceProto,
} from '@buf/cosmos_cosmos-sdk.bufbuild_es/cosmos/feegrant/v1beta1/feegrant_pb';

const CREATE_MSG_GRANT_ALLOWANCE_TYPES = (params: MsgGrantAllowanceParams) => ({
  MsgValue: [
    { name: 'granter', type: 'string' },
    { name: 'grantee', type: 'string' },
    { name: 'allowance', type: 'TypeAllowance' },
  ],
  TypeAllowance: [
    { name: 'type', type: 'string' },
    { name: 'value', type: 'TypeAllowanceValue' },
  ],
  TypeAllowanceValue: [
    ...(params.basicAllowance.spendLimit.length ?
      [ { name: 'spend_limit', type: 'TypeAllowanceValueSpendLimit[]' } ] : []),
    { name: 'expiration', type: 'string' },
  ],
  ...(params.basicAllowance.spendLimit.length && {
    TypeAllowanceValueSpendLimit: [
      { name: 'denom', type: 'string' },
      { name: 'amount', type: 'string' },
    ],
  }),
});

export interface MsgGrantAllowanceParams {
  granteeAddress: string;
  basicAllowance: BasicAllowance;
}

export const createTxMsgGrantAllowance = (
  context: TxContext,
  params: MsgGrantAllowanceParams,
): TxPayload => {
  const typedData = createEIP712MsgGrantAllowance(context, params);
  const cosmosMsg = createCosmosMsgGrantAllowance(context, params);
  return createTransactionPayload(context, typedData, cosmosMsg);
};

const createEIP712MsgGrantAllowance = (context: TxContext, params: MsgGrantAllowanceParams): EIP712TypedData => {
  const types = generateTypes(CREATE_MSG_GRANT_ALLOWANCE_TYPES(params));
  const message = createMsgGrantAllowance(context, params);
  return { types, message };
};

const createCosmosMsgGrantAllowance = (context: TxContext, params: MsgGrantAllowanceParams): any => {
  const message = new MsgGrantAllowance({
    granter: context.sender.accountAddress,
    grantee: params.granteeAddress,
    allowance: createAnyMessage({
      message: new BasicAllowanceProto({
        spendLimit: params.basicAllowance.spendLimit,
        expiration: new Timestamp({
          seconds: BigInt(params.basicAllowance.expiration?.seconds.toNumber() || 0),
          nanos: 0,
        }),
      }),
      path: BasicAllowanceProto.typeName,
    }),
  });
  return { message, path: MsgGrantAllowance.typeName };
};

function createMsgGrantAllowance(context: TxContext, params: MsgGrantAllowanceParams): object {
  const expiration = new Date();
  expiration.setTime((params.basicAllowance.expiration?.seconds.toNumber() || 0) * 1000);
  return {
    type: 'cosmos-sdk/MsgGrantAllowance',
    value: {
      granter: context.sender.accountAddress,
      grantee: params.granteeAddress,
      allowance: {
        type: 'cosmos-sdk/BasicAllowance',
        value: {
          expiration: expiration.toISOString().replace('.000Z', 'Z'),
          ...(params.basicAllowance.spendLimit.length && { spend_limit: params.basicAllowance.spendLimit }),
        },
      },
    },
  };
}

